// Shared live-stream engine (LP-H / MR-75). Single home for attaching/detaching
// the hls.js live engine to an <audio>/<video> element — it REPLACES the
// duplicated inline `viblast(el).setup()/.stop()` calls the players carried. v4
// Player.vue was the first caller; v1/v2/v3 migrated off Viblast in later MRs
// (LP-1..LP-4), and the Viblast global itself was removed at LP-4/MR-78.
//
// hls.js is a LOADED GLOBAL (window.Hls), NOT a bundled import. It is VENDORED
// INTO THE DIST by vite.main.config.mjs's `emit-vendored-hls` plugin and injected
// by src/hls_loader.js under a bare relative name (it used to be fetched from an
// S3 mirror that no CI job had ever populated — see that file's header, and note
// its Path B caveat) — so the single-IIFE Vite bundle and its gzip budget stay
// UNCHANGED (scripts/verify-artifact-shape.js). Inlining hls.js into the bundle
// is a separate, still-deferred change; until then this module never imports
// hls.js and reads it only through window.Hls.
//
// The engine deliberately mutates the media element it is handed (it attaches
// the hls.js instance as `__hlsInstance` and records the source in
// `dataset.hlsSrc`) — that element-side-effect IS the contract, mirroring how
// the old `viblast(el)` global mutated the element. That is why no-param-reassign
// is disabled for this file.
/* eslint-disable no-param-reassign */
import { pushTelemetryEvent } from '../core/telemetry';
// The NotAllowedError counter (F-3). Same reporter Player.vue's play() and Menu.vue's
// clipboard copy use — see startPlayback for why this call site was missing.
import { reportNotAllowedIfApplicable } from '../core/faro';
import { HLS_ASSET_NAME } from '../hls_loader';

// Minimal hls.js live config. Deliberately EMPTY: hls.js's default config
// already tracks the live edge, so we set no startPosition / liveSyncPosition
// that could defeat a future drop-to-live. HE-AAC audio-only streams are
// handled transparently by hls.js's built-in transmuxer — there is NO hls.js
// equivalent of the old Viblast `forceHEAAC: true` flag, so it simply drops.
const LIVE_CONFIG = {};

// Bound hls.js media-error recovery so a persistently-broken stream can't drive
// an ERROR -> recoverMediaError -> ERROR loop; after this many consecutive fatal
// media errors the engine gives up and tears the session down.
const MAX_MEDIA_RECOVERY = 3;

// A fatal NETWORK_ERROR is retried UNBOUNDED, on purpose — this is a live engine and a
// transient blip must never take an embed down. But retrying SILENTLY forever is how a
// permanently-dead stream URL stays invisible, which is the PS-22 failure mode this
// module's reporting exists to end: it was the only fatal branch that reported nothing
// at all, while MEDIA_ERROR reports on exhaustion and every other fatal reports and
// tears down (Phase 2). Report once when the count reaches a number no blip reaches.
// Deliberately NOT a give-up threshold: retries continue after it.
const NETWORK_RETRY_REPORT_AT = 10;

// PS-23 — a dead engine must not be indistinguishable from a working one.
//
// This module's swallow-everything contract is what turned a missing build
// artefact into a fleet-wide invisible outage lasting the entire port (PS-22):
// the engine 403'd, window.Hls stayed undefined, the `no engine` branch below
// no-op'd, and there was nothing to see in a console, a dashboard or CI.
//
// The swallow itself is still right — a telemetry or engine hiccup must never
// take the embed down, and hls.js emits fatal errors per fragment on a marginal
// stream, so reporting from inside the ERROR handler unfiltered would bury the
// console and flood Faro. What was wrong was reporting NOTHING. So: report on
// the give-up states a listener can do nothing about, rate-limited per
// reason+url.
//
// 🪤 DO NOT RE-LIST THE REASONS HERE. This comment has now drifted FOUR times: it
// once said "the two states … ONCE per page load" (both false), the register said
// four (omitting `fatal_unrecoverable`), then this list said FIVE and a later review
// found it omitted `manifest_unrecoverable`, which D-139 had added in the same MR
// that corrected the earlier drift. Each correction was itself a hand-maintained
// list, so each one rotted the next time a reason was added.
//
// The AUTHORITY is now derived, not written: `test/unit/hls-engine-notifies-caller.js`
// parses every `reportEngineFailure(` call site out of this module, requires each
// reason to be a string literal, and fails if its census does not classify exactly
// the set it finds. So the enumeration lives in one place that CANNOT drift, and the
// way to read the current set is to run that spec — or
// `grep -n "reportEngineFailure(" src/utils/hls_engine.js`.
//
// ⚠️ And note the reasons are NOT all terminal, which every prose version of this
// list blurred: `network_retry_flood` reports while the engine keeps retrying, and
// `native_hls_failed` is reported from a listener on the element, so the component
// has already been notified natively. The census records that distinction per reason.
//
// Keyed by reason AND stream url, with a TIME WINDOW rather than a permanent
// one-shot. Module-scoped on purpose: the suppression must span every attach,
// since episode-switch and drop-to-live both re-attach.
//
// ⚠️ This was a permanent per-reason flag until 2026-08-07. Independent review
// measured the consequence: station A's stream dies, is detached, and a
// DIFFERENT element on a DIFFERENT url dies the same way -> the second outage is
// never reported at all. Multi-station embeds are real (app.vue:1159 already
// records that `stations.length > 1` misattributes), and a signal an operator
// polls must not go permanently quiet after its first event. A window keeps the
// anti-flood property that motivated the one-shot — hls.js emits fatal errors
// per fragment on a marginal stream — without hiding an outage an hour later.
const REPORT_WINDOW_MS = 60000; // no numeric separator: src/ is pinned to ES2015
const reportedFailures = {};

// 🔴 AND THE KEY IS DERIVED FROM A URL THE WIDGET DOES NOT CHOOSE (D-36).
//
// `getLiveStreamUrl` (utils.js) fetches the station manifest, regexes a second
// url out of it, fetches THAT, and resolves `resp2.url` — the final url after
// the CDN's redirects. A CDN is free to vary it per resolve: a session token, a
// cache-buster, a rotating edge hostname. Every re-attach re-resolves.
//
// Anti-flood is NOT what that breaks — hls.js's per-fragment error storm happens
// inside one attach, whose url is fixed for its lifetime. What it breaks is the
// map: a new key per resolve and nothing ever removed, growing for the life of
// the embed. So two bounds, plus a normalised key:
//   * strip the query and fragment, where per-resolve tokens live, so the common
//     churn stops minting keys at all;
//   * drop entries that have expired — they can never suppress again;
//   * cap what survives, as a backstop against churn the stripping misses (a
//     rotating edge hostname).
//
// ⚠️ THE CAP IS BEST-EFFORT ACROSS STREAMS, AND THIS COMMENT USED TO OVERSTATE
// IT ("the flood case never churns" — a per-stream argument about a map that is
// shared by every stream in the realm). Independent review of !229 measured the
// boundary: with MORE distinct live keys than the cap, every report evicts the
// head, the head is a key that is still firing, and suppression does not
// degrade — it inverts. 32 keys x 10 rounds = 32 reports; 33 x 10 = 330.
//
// Two measured facts bound how much that matters, and both are worth knowing
// before anyone "fixes" it by raising the number:
//   * The per-fragment flood this module was built to prevent is NOT held off by
//     this map. Every report site is terminal within one attach — the hls.js
//     give-up branches all call teardownHls() (THREE of them since D-139 added
//     `manifest_unrecoverable`; this line said "both" until a review counted) and
//     the native listener is {once:true} — so
//     300 fatal errors through one attached instance produce ONE report at any
//     cap, including 1. The map only suppresses ACROSS attaches, and attaches
//     are user-paced (a play click, or drop-to-live on `ended`); there is no
//     retry loop.
//   * Each embed is its own iframe document, so this map is per embed, not per
//     page. Reaching the boundary needs one embed carrying >32 live keys
//     (reason x stream), not 33 widgets on a directory page.
// So the number is a memory bound, not a correctness one. Raising it buys nothing
// reachable; lowering it is what would hurt.
//
// 🪤 D-49 — this comment used to end "which is why the tests pin both edges". FALSE at
// the time: the only cap test (hls-engine-observability.spec.js, 40 distinct urls)
// exercised the UPPER edge alone, so a well-meaning reduction of this constant passed
// CI — 1, 2 and 39 all survived 844/844.
//
// It then said "It cannot be pinned without making the value injectable." Also false,
// and a more interesting error: eviction is observable at the BOUNDARY from outside the
// module, which is all a bound needs. 32 distinct keys evict nothing, so the first
// stream stays suppressed; 33 evict one, so it reports again. Two tests either side of
// the edge pin this value EXACTLY, with no injection and no export — and 1, 31, 33 and
// 39 each now red one of them.
//
// 🔑 Twice this comment claimed coverage that did not exist, and the second time it
// claimed the coverage was impossible. "It cannot be tested" is a hypothesis, not a
// property of the code.
const MAX_TRACKED_STREAMS = 32;

// Deliberately NOT dropping the host as well. A CDN that identifies the station
// in the hostname and serves a generic path (`/index.m3u8`) would otherwise
// collapse two stations onto one key, and muting station B because station A
// failed is the exact regression MED-5 fixed.
const streamKey = (url) => {
  if (!url) return '';
  return String(url).split('#')[0].split('?')[0];
};

// A stamp is usable only if it is in the past AND inside the window. `nowMs`
// prefers the monotonic clock, but on the `Date.now` fallback a backwards clock
// correction would otherwise make `t - last` hugely negative and mute the stream
// until the clock caught up.
const isFresh = (last, t) => last !== undefined && t >= last && (t - last) < REPORT_WINDOW_MS;

const pruneReportedFailures = (t) => {
  const keys = Object.keys(reportedFailures);
  for (let i = 0; i < keys.length; i += 1) {
    if (!isFresh(reportedFailures[keys[i]], t)) delete reportedFailures[keys[i]];
  }
  // Backstop. String keys keep insertion order, so the head is the oldest — and
  // the caller deletes before re-inserting, so a re-stamped key moves to the
  // tail rather than keeping its original position and being evicted first.
  const fresh = Object.keys(reportedFailures);
  for (let i = 0; i < fresh.length - MAX_TRACKED_STREAMS; i += 1) {
    delete reportedFailures[fresh[i]];
  }
};

// Monotonic where available: Date.now() jumps when the host page's clock is
// corrected, which would either mute reporting for a long stretch or defeat the
// window entirely. performance.now() is immune and present in every target.
const nowMs = () => {
  try {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      return performance.now();
    }
  } catch (e) { /* fall through */ }
  return Date.now();
};

// Was the engine global actually there? This is the single most useful datum for
// whoever reads the signal, and it keeps the message HONEST: the native-HLS and
// hls.js-internal failures below can happen with a perfectly healthy engine, so
// a message that always blamed the vendored asset would send an operator hunting
// a missing file that is present (review fold).
const engineLoaded = () => {
  try {
    return typeof window !== 'undefined' && typeof window.Hls === 'function';
  } catch (e) {
    return false;
  }
};

// What the engine looked like when we CHOSE a branch — parked ON THE ELEMENT as
// `__hlsEngineAtAttach`, beside `__hlsInstance` and `__hlsNativeErrorHandler`.
//
// It was a module-scoped map keyed by url until 2026-08-07, which was wrong
// twice (D-36). It leaked — an entry per distinct resolved url, written on EVERY
// attach whether or not anything ever failed, and never removed. And it was
// shared: a page holding two players for one station (a v2 `LiveAudioPlayer`
// alongside a v4 `Player`, or two embeds of the same station) had the second
// attach overwrite the first's reading, so the first element's failure reported
// the OTHER element's branch — measured, and it defeats the whole point of the
// field. The reading describes one attach of one element, so the element is
// where it belongs; per-element state cannot accumulate.
//
// `engineLoaded()` sampled at report time is not the same question and can
// disagree: the engine is absent at attach (so Chrome, whose canPlayType answers
// 'maybe', takes the NATIVE branch), the async vendored <script> then lands, and
// the eventual decode error reports "loaded" — blaming a file that is present
// while the branch was taken because it was not. Measured; independent review of
// !219, MED-1. Recording the attach-time reading keeps the message able to
// explain the branch, which is the whole point of the field.
const ENGINE_AT_ATTACH = '__hlsEngineAtAttach';

const reportEngineFailure = (reason, detail, url, engineAtAttach) => {
  try {
    const key = `${reason} ${streamKey(url)}`;
    const t = nowMs();
    if (isFresh(reportedFailures[key], t)) return;

    const loaded = engineLoaded();
    // Order matters. The suppression stamp used to be written BEFORE the two
    // emits, so a single throw on the console channel burned the key and
    // permanently destroyed the telemetry signal for that reason with no retry
    // possible (independent review, !219 MED-3 — measured). Emit first, stamp
    // last: a throw leaves the key unset and the next occurrence retries.
    //
    // 🔴 AND ONE `try` PER CHANNEL (D-37). Sharing a single `try` across both
    // emits made them fail together. That is survivable for a TRANSIENT throw —
    // the case the MED-3 test pins — but the throw that matters in the wild is
    // DETERMINISTIC: a host page that wraps `console.error`, which analytics
    // shims, error-reporting SDKs and ad frameworks all do, and which is exactly
    // the hostile-host behaviour this module exists to be defensive about. Such
    // a wrapper never recovers, so a shared `try` killed telemetry for the life
    // of the page AND, because the stamp is last, left the key unset — so the
    // rate limiter never engaged and every occurrence redid the work and threw
    // again. The channels are independent; they must fail independently.
    let emitted = false;
    try {
      // eslint-disable-next-line no-console
      console.error(
        `[futuri-widget] live audio unavailable (${reason}): ${detail || 'no further detail'} `
        + `(engine ${HLS_ASSET_NAME}: ${loaded ? 'loaded' : 'NOT loaded'})`
      );
      emitted = true;
    } catch (e) { /* a booby-trapped console must not take telemetry with it */ }
    try {
      pushTelemetryEvent('widget_engine_failure', {
        reason,
        asset: HLS_ASSET_NAME,
        // Both readings. `engine_loaded` is sampled HERE, at report time, and it
        // lies in exactly the race this module exists to diagnose: the engine is
        // absent at attach (so Chrome takes the native branch), the async vendored
        // <script> lands, the decode error fires, and the report then says
        // "loaded" while the branch was chosen because it was not.
        // `engine_loaded_at_attach` is the one that explains the branch taken.
        engine_loaded: loaded,
        engine_loaded_at_attach: engineAtAttach === undefined
        ? 'unknown'
        : String(engineAtAttach),
        // Attribution. Without the url a multi-station embed's failure is
        // indistinguishable from station one's — Faro stamps only stations[0].
        // `station` itself is stamped onto every event by core/telemetry, which
        // is where the widget's station is registered (setTelemetryStation).
        //
        // 🔴 D-164 — STRIPPED, through the same helper as the suppression key.
        // This module already asserts, ~150 lines up, that the query is where
        // "per-resolve tokens live" — that is the stated reason `streamKey()`
        // strips it. The event body then sent the raw url anyway, so the one thing
        // the module names as sensitive was the one thing shipped to Faro.
        // MEASURED on the `no_engine` path before this: the emitted event carried
        // `token=SECRET-abc123&exp=99` intact. Whether a real CDN token appears
        // there in production is unmeasured — but a module that says tokens live
        // in the query must not forward the query, and the attribution this field
        // exists for (which station, which edge) is entirely in the part kept.
        url: streamKey(url),
        detail: detail || '',
      });
      emitted = true;
    // ⚠️ Unreachable today, and deliberately kept. `pushTelemetryEvent` guards
    // its own transport, so a dead/hostile collector is absorbed there and never
    // throws into this frame — measured, and removing this whole try/catch
    // survives the full unit suite because nothing can enter it. It is the
    // belt to telemetry.js's braces: the day that blanket catch is narrowed,
    // this becomes load-bearing with no other guard behind it.
    } catch (e) { /* and a dead collector must not take the console with it */ }
    // Stamp only if an emit call RETURNED. Stamping unconditionally would trade
    // D-37 for the bug MED-3 fixed: both channels down, nothing emitted, and
    // the key burned for 60s anyway.
    //
    // ⚠️ "returned" is weaker than "got out", and this comment used to claim the
    // stronger thing. Measured (independent review of !229): `pushTelemetryEvent`
    // wraps its whole body in try/catch and cannot throw, so in shipped code
    // `emitted` is ALWAYS true here and this branch is never taken false. When
    // Faro has not initialised yet the event is BUFFERED and flushed by
    // app.vue's initFaro()/flushTelemetry(), so it does get out; on an embed
    // that never inits Faro (a synthetic agent) there was no collector to reach
    // in the first place. The branch is kept because it is load-bearing the
    // moment telemetry.js stops swallowing — which is a contract, not a law.
    if (emitted) {
      // Delete before re-inserting so a re-stamped key moves to the tail of the
      // insertion order, which is what makes the cap's "head is oldest" true.
      delete reportedFailures[key];
      reportedFailures[key] = t;
      pruneReportedFailures(t);
    }
  } catch (e) {
    // Observability must never surface to the embed — the same contract as the
    // attach guard this reports on. A reporter that can break playback is worse
    // than the silence it replaces.
  }
};

// Tear down any hls.js session on an element: destroy the instance, drop the
// reference, and clear the source bookkeeping. Shared by detachLiveStream
// (public) and attachLiveStream (stop-before-(re)attach + the fatal-error
// give-up). Throw-safe — the iOS/engine-quirk reason the old Viblast stop was
// try/caught.
/**
 * D-134 — tell the COMPONENT when the live engine has given up.
 *
 * hls.js handles fatal errors entirely inside this module: every give-up path
 * calls `reportEngineFailure(...)` (telemetry) and `teardownHls(...)` (cleanup),
 * and then stops. `grep -n "dispatchEvent\|new Event" src/utils/hls_engine.js`
 * returned NO MATCHES before this, and `attachLiveStream`'s only option is
 * `{ autoplay }` — so nothing reached the caller at all.
 *
 * MEASURED consequence: on the live path `isLoading` stayed true forever after the
 * engine gave up, and `mediaEl.error` was **null**, so the spinner wedge D-105
 * closed for on-demand was still fully open for every live stream — and
 * `onMediaError` could never fire there, because no `error` event was ever raised.
 *
 * 🪤 CORRECTED (D-144, measured in real chromium against a 404 manifest): that last
 * clause is FALSE for the MSE path. Where hls.js had attached a MediaSource,
 * `teardownHls`'s `hls.destroy()` revokes the object URL while leaving `src`
 * pointing at it, so the browser fires a NATIVE `error` — which `@error` already
 * routes to `onMediaError`. A native event IS raised there.
 *
 * 🪤 CORRECTED AGAIN, and the first correction overshot. It concluded "the repair is
 * delivered by teardownHls, not by the notify … the synthetic dispatch is
 * belt-and-braces here" — from a 2x2 with a cell missing. Completing it:
 *   notify removed, teardown kept  -> e2e GREEN
 *   teardown removed, notify kept  -> e2e GREEN   <-- the cell nobody ran
 *   both removed                   -> e2e RED
 * So the two are INDEPENDENTLY SUFFICIENT and mutually redundant on this path:
 * each alone repairs the component, neither is necessary while the other stands.
 * "Belt-and-braces" was the right instinct about redundancy and the wrong claim
 * about direction — it named one mechanism as the real one when the measurement
 * cannot distinguish them.
 *
 * 🔑 Where the dispatch is the ONLY mechanism: the branches that attach NOTHING —
 * `no_engine` and `attach_threw` before `attachMedia`. There is no MediaSource to
 * revoke, so no native event can ever be raised, and those two reported telemetry and
 * told the component nothing at all until D-143. Keep the dispatch: the reason it
 * exists is the paths where nothing else can speak.
 *
 * 🪤 WHICH ENGINES REACH `no_engine` — measured, because an earlier revision of this
 * docstring called it "the shipped off-master state" flatly, six lines above the
 * comment at the native-HLS branch that says the opposite. `canPlayType('application/
 * vnd.apple.mpegurl')`, measured in this repo's own playwright browsers:
 *   chromium -> 'maybe'  => takes the NATIVE branch; `no_engine` is UNREACHABLE
 *   firefox  -> ''       => takes `no_engine`
 * So off-master (!207, the vendored mirror never uploaded) the dead-engine path is
 * `no_engine` on FIREFOX, and the native branch on Chrome — where the failure is an
 * asynchronous native `error` instead. Real Safari has native HLS and also takes the
 * native branch. Saying "every live embed" names the wrong branch for the majority
 * engine, which is the exact error PS-22/PS-23 made and that :553-560 exists to
 * correct. Qualify the claim or drop it.
 *
 * 🔑 Dispatching a real `error` event on the media element, rather than adding a
 * callback option, is deliberate: all four audio-bearing components already bind
 * `@error` (v3/v4 from D-105, v2/v1 from D-133), so one change reaches every one of
 * them and any future component gets it for free. A callback would have to be
 * threaded through `playLiveStream -> setupHlsEngine -> attachLiveStream` in each
 * component — four call sites, which is precisely the shape that has gone wrong
 * six times in this work.
 *
 * ⚠️ `mediaEl.error` stays null on this path, because nothing native failed. That
 * is why every handler bound to it must not read `event.target.error` — the same
 * constraint D-105 established for the `<source>` path.
 */
const notifyUnrecoverable = (mediaEl) => {
  try {
    if (!mediaEl || typeof mediaEl.dispatchEvent !== 'function') return;
    mediaEl.dispatchEvent(new Event('error'));
  } catch (e) {
    // A synthetic dispatch must never be able to break teardown.
  }
};

/**
 * 🔴 D-151 — the engine's OWN autoplay, with the rejection handled.
 *
 * `attachLiveStream` had two bare `mediaEl.play();` calls under `opts.autoplay`, one
 * per branch. They were the only two of the 13 `.play()` sites in `src/` with no
 * rejection handler of any kind — and the same two the D-142 census dropped when it
 * claimed "nine sites", which is how they stayed invisible.
 *
 * 🔑 ONE helper rather than a `.catch` added at each site, deliberately. The whole
 * finding is "N call sites, one missed"; a shared helper makes a third unhandled site
 * structurally impossible instead of relying on the next person to remember. Same
 * lesson as D-133.
 *
 * ⚠️ This is NOT a give-up reason and must not become one. The stream is attached and
 * the element simply declined to start — most often `NotAllowedError`, which is
 * ordinary browser autoplay policy, not a failure. The listener still has a working
 * play control. Reporting it through `reportEngineFailure` would put a routine browser
 * policy decision in the same channel as a dead engine, which is what PS-23's
 * rate-limited give-up reporting exists to keep clean. A warn is proportionate: it is
 * visible when someone is looking and it costs nothing when they are not.
 *
 * @param {HTMLMediaElement} mediaEl the element to start
 * @return {void}
 */
const startPlayback = (mediaEl) => {
  if (!mediaEl || typeof mediaEl.play !== 'function') return;
  try {
    const p = mediaEl.play();
    // Older engines return undefined rather than a promise (the same
    // backwards-compatibility case the Players' playMedia guards).
    if (p && typeof p.catch === 'function') {
      p.catch((e) => {
        // eslint-disable-next-line no-console
        console.warn('Live autoplay was rejected', e);
        // 🔑 AND COUNT IT. This was a console.warn and nothing else, which made LIVE
        // autoplay denials countable nowhere: D-207 reports the denial at the four
        // on-demand sites, so a dashboard showing zero for a station whose failures are
        // all live reads as "no problem" rather than "not instrumented". Same reporter,
        // same filter (isNotAllowedRejection), so a non-autoplay rejection is still
        // only a warning.
        reportNotAllowedIfApplicable(e);
      });
    } else if (p && typeof p.then === 'function') {
      // 🔴 D-176 — a thenable with NO `.catch` still rejects, and the guard above
      // silently skipped it, reintroducing the unhandled rejection this helper exists to
      // remove. Not hypothetical in this codebase's own terms: D-37 records that a host
      // page can replace promise methods, and `play()` returns whatever the host's
      // patched implementation hands back. `.then(null, handler)` is the same contract
      // without requiring `.catch` to exist.
      p.then(null, (e) => {
        // eslint-disable-next-line no-console
        console.warn('Live autoplay was rejected', e);
        // Counted on the thenable path too — a host page that patched play() to return
        // a bare thenable is exactly the population whose denials would otherwise be
        // invisible twice over (D-176 for containment, this for counting).
        reportNotAllowedIfApplicable(e);
      });
    }
  } catch (e) {
    // A throwing play() is the iOS quirk this module already swallows elsewhere.
  }
};

const teardownHls = (mediaEl) => {
  try {
    if (!mediaEl) return;
    if (mediaEl.__hlsInstance) {
      // D-74: destroy() gets its OWN guard. It used to sit first inside the
      // single try/catch below, so a throw skipped every line under it — the
      // null-out, the dataset.hlsSrc delete AND the removeEventListener — which
      // defeats all three of the native branch's self-disarm mechanisms at once.
      // The bookkeeping is this function's whole purpose and must not be
      // contingent on the engine cooperating; a throwing stop is the exact quirk
      // the outer try/catch was added for, so it is the one case that has to
      // still clean up.
      try {
        mediaEl.__hlsInstance.destroy();
      } catch (e) {
        // Engine quirk — swallowed, exactly as before. The difference is that
        // the lines below now run anyway.
      }
      mediaEl.__hlsInstance = null;
    }
    // 🔴 D-148 — clear the `src` WE caused, because hls.js does not.
    //
      // hls.js's `detachMedia` is asymmetric (v1.6.16). Cite the SYMBOL, not the line:
      // `dist/hls.js` is not a path in this repo — hls.js lives in node_modules and its
      // line numbers move on every bump, so the old line-range reference into that file
      // pointed at nothing checkable:
      //   revokeObjectURL(_objectUrl)        UNCONDITIONAL
      //   removeAttribute('src') + load()    only `if (mediaSrc === _objectUrl)`
      // and `mediaSrc` is `media.querySelector('source') || media`
    // then `.src`. All four audio components render a `<source>` child — that is
    // D-105's own fix — so the getter returns the SOURCE's url, the compare fails,
    // hls.js logs "media|source.src was changed by a third party - skip cleanup",
    // and the revoked blob stays in the `src` IDL attribute. Since `src` wins over
    // `<source>` children in resource selection, the dead blob SHADOWS the live url:
    // v2's D-133 retry (`audioPlayer.load()`) re-selected it, errored code 4 in about
    // a millisecond and issued NO request, forever. Pressable, never effective.
    //
    // 🔑 Gated on `blob:` and nothing else, because that is the only discriminator
    // that cannot misfire. `teardownHls` runs on far more than the give-up path —
    // stop-before-(re)attach, all five terminal branches, and `detachLiveStream` from
    // every `beforeUnmount`, from podcast.vue's m4a bypass and from
    // LiveAudioPlayer's `audioFileSrc` watcher — where `src` legitimately holds a
    // real m4a or manifest url that must survive. Nothing in `src/` mints an object
    // URL, so a `blob:` here can only be the MediaSource url our own engine attached.
    //
    // 🔑 That premise is DERIVED, not asserted here: `test/unit/no-object-url-in-src.spec.js`
    // reds if any file under `src/` calls `createObjectURL`, because this guard clears
    // any blob it finds and a second minter would make it delete somebody else's src.
    // Deliberately phrased as a pointer rather than as a grep result — a grep quoted
    // in a comment records a measurement that was true once, and this one would have
    // been self-refuting the moment it was written, since the quoted pattern then
    // matches this file.
    //
    // 🪤 Deliberately NO `load()`, and the omission is the fix rather than an
    // oversight. Mirroring hls.js's own `removeAttribute('src') + load()` is the
    // obvious shape and it is WORSE here. MEASURED both ways in real chromium with
    // the real engine (media-event order recorded on the element):
    //
    //   no load()  -> abort, emptied, loadstart, error(null), error(code 4)
    //                 2 manifest requests total; element error stays 4; srcAttr null
    //   with load() -> abort, emptied, loadstart, error(null), abort, emptied, loadstart
    //                 3 manifest requests; element error back to NULL; networkState 3
    //
    // Three measured costs, in order of how much they matter:
    //   1. The extra `load()` issues a NETWORK REQUEST from teardown. `teardownHls`
    //      runs on far more than a give-up — `beforeUnmount`, the m4a bypass, every
    //      stop-before-attach — so that is a fetch of a stale `<source>` on elements
    //      that are being torn down or about to be re-pointed. (Third hit above.)
    //   2. `load()` RESETS `mediaEl.error` to null, erasing the only native evidence
    //      that the stream failed. v2 arms `mediaFailed` from an `error` event; with
    //      the load, the arming instead depends on the new load ALSO failing, which a
    //      stalled-but-not-failing edge may never deliver.
    //   3. `load()` fires `emptied`, which v2 binds (`LiveAudioPlayer.vue:9`) to
    //      `resetData()` -> `mediaFailed = false`. That is the D-133 state exactly:
    //      `canPlay` false AND the latch disarmed is the one combination where
    //      `onPlayToggleButton` early-returns and the control does nothing at all.
    //
    // 🪤 And the prediction that sent me looking was only PARTLY right, which is worth
    // recording. It was "with `load()` the control is dead — D-133 resurrected". The
    // measurement says the dead window is BOUNDED, not permanent: the load it starts
    // 404s, that failure reaches `@error` on the `<source>` child, and `mediaFailed`
    // re-arms. So (3) is real but self-repairing on a fast failure, and the durable
    // reasons to omit the load are (1) and (2). Recorded because "the obvious fix
    // resurrects D-133" is the kind of clean story that survives past its evidence.
    //
    // Nothing needs the load anyway: v2's retry calls `load()` itself, and every
    // other caller assigns a fresh src, which re-runs resource selection on its own.
    //
    // ⚠️ Only the non-ManagedMediaSource attach is affected, which is the majority
    // engine. hls.js takes `media.src = objectUrl` (`:19320`) unless `appendSource`,
    // which is true only for ManagedMediaSource (`:19161`, iOS 17+ Safari); there it
    // removes `src`, replaces the `<source>` children with its own and cleans them
    // up symmetrically on detach. So this guard simply never fires on that path.
    if (typeof mediaEl.getAttribute === 'function'
      && typeof mediaEl.removeAttribute === 'function') {
      const attachedSrc = mediaEl.getAttribute('src');
      if (attachedSrc && attachedSrc.indexOf('blob:') === 0) {
        mediaEl.removeAttribute('src');
      }
    }
    // Clear regardless of an instance — a native (no-instance) attach also sets
    // dataset.hlsSrc, so an instance-gated clear would leave it stale.
    if (mediaEl.dataset) delete mediaEl.dataset.hlsSrc;
    // Drop the native-path error listener (review fold — see the attach site).
    if (mediaEl.__hlsNativeErrorHandler) {
      if (typeof mediaEl.removeEventListener === 'function') {
        mediaEl.removeEventListener('error', mediaEl.__hlsNativeErrorHandler);
      }
      mediaEl.__hlsNativeErrorHandler = null;
    }
  } catch (e) {
    // Swallow (mirrors the attach guard).
  }
};

/**
 * Attach a live HLS stream to a media element via hls.js (or native HLS on
 * iOS Safari). Throw-safe: an absent/throwing `window.Hls` — or an iOS quirk,
 * the reason the old Viblast call was try/caught — is swallowed rather than
 * surfaced. Does NOT auto-play unless `opts.autoplay` is truthy; the v4 caller
 * plays explicitly (matching the Viblast `autoplay: false` + explicit-play
 * pattern).
 *
 * @param {HTMLMediaElement} mediaEl the `<audio>`/`<video>` element
 * @param {String} url the live HLS manifest URL (a `.m3u8`)
 * @param {Object} [opts] options; `opts.autoplay` (default false) plays after attach
 * @return {void}
 */
export const attachLiveStream = (mediaEl, url, opts) => {
  const options = opts || {};
  // A falsy element is an UNMOUNT RACE, not an engine failure.
  //
  // Every caller does `attachLiveStream(this.$refs.player, ...)` inside a
  // `getLiveStreamUrl(...).then(...)`; if the component unmounted while that
  // fetch was in flight, `$refs.player` is undefined. Pre-!219 that was a
  // deliberate silent no-op. !219 turned it into `attach_threw` — a
  // "live audio unavailable" alarm for an embed the user has already navigated
  // away from — and permanently burned the reason key with it. Return before
  // the reporting path, as before. (Independent review of !219, MED-5.)
  if (!mediaEl) return;
  // 🔴 D-51 — the snapshot moves OUTSIDE the try, and that is the whole fix.
  //
  // It used to be the first statement INSIDE it, which reads as "before any branch is
  // chosen" and is not the same thing: `window.Hls` is a host-page global, and reading a
  // getter a host has defined can throw. When it did, control reached the outer catch
  // with the expando never written — so `attach_threw` reported whatever the PREVIOUS
  // attach on this element left behind. Live streams re-attach constantly (episode
  // switch, drop-to-live), so the stale value is the common case, not a corner.
  //
  // The consequence is a lie in the one field that explains which branch was taken:
  // `engine_loaded_at_attach` is the reading an operator uses to tell "the vendored
  // engine never arrived" (PS-22) from "the engine was there and broke". Reporting the
  // last attach's answer for this one points the diagnosis at the wrong failure.
  //
  // Outside the try, it cannot be skipped by a throw. `typeof` on a getter-backed
  // property can still throw, so it gets its own guard rather than trusting it.
  try {
    mediaEl[ENGINE_AT_ATTACH] = typeof window !== 'undefined'
      && typeof window.Hls === 'function';
  } catch (e) {
    // A host global whose getter throws is not a reading we can take — say "unknown"
    // rather than inherit the previous attach's answer.
    mediaEl[ENGINE_AT_ATTACH] = undefined;
  }
  try {
    const Hls = (typeof window !== 'undefined') ? window.Hls : undefined;

    // Stop-before-(re)attach: tear down any session already on this element so a
    // re-attach can never orphan a live hls.js instance (mirrors the old
    // `viblast(el).stop()` that always preceded `.setup()`).
    teardownHls(mediaEl);

    // Is the MSE engine usable? Isolated in its own try so a THROWING
    // Hls.isSupported() (a broken global) still falls through to native HLS
    // rather than no-op'ing the whole attach.
    let hlsSupported = false;
    try {
      hlsSupported = !!(Hls && Hls.isSupported && Hls.isSupported());
    } catch (e) {
      hlsSupported = false;
    }

    // Primary path: hls.js Media Source Extensions engine.
    if (hlsSupported) {
      const hls = new Hls(LIVE_CONFIG);
      // 🔴 D-145 — park the reference IMMEDIATELY, before anything that can throw.
      //
      // This used to sit ~100 lines below, after the ERROR handler registration. A
      // throw anywhere in between — `attachMedia`, an `hls.on(...)` registration —
      // reached the `attach_threw` catch with a fully constructed engine that NOTHING
      // held a reference to: `teardownHls` walks `mediaEl.__hlsInstance`, found
      // nothing, and the instance kept its MediaSource and its network activity until
      // GC. On a live stream that means it goes on buffering a stream nobody is
      // listening to.
      //
      // 🔑 D-143's teardown in that catch does NOT cover this, and it was checked when
      // that teardown was written: teardown is load-bearing there for `dataset.hlsSrc`
      // and the parked native listener, both of which are reachable FROM the element.
      // The instance was the one thing that was not. Parking first is what makes the
      // catch able to clean up at all.
      mediaEl.__hlsInstance = hls;
      // 🔴 D-178 — the source bookkeeping moves up here WITH the instance, and the
      // assignments that used to sit after the handler registrations are gone.
      //
      // D-145 parked the instance early so `attach_threw` could clean it up, but left
      // the ORIGINAL `__hlsInstance = hls` / `dataset.hlsSrc = url` pair below the
      // `hls.on(...)` calls. Same value, so it looked like harmless duplication — until
      // something tears down DURING those registrations, which is what D-161's fix does
      // when `loadSource` throws inside the MEDIA_ATTACHED callback. The teardown nulled
      // the instance and
      // deleted the dataset, and then the lines below RESURRECTED both, handing the
      // caller a torn-down engine that `teardownHls` will never find again.
      // MEASURED: with the re-park in place, the census's "leaves no hls session parked
      // on the element" check fails for `load_source_threw`.
      mediaEl.dataset.hlsSrc = url;
      hls.attachMedia(mediaEl);
      // hls.js requires loadSource only AFTER the media element is attached.
      // This callback fires ASYNC, outside the outer try — guard it directly.
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        try {
          hls.loadSource(url);
        } catch (e) {
          // 🔴 D-161 — this used to be a BARE swallow, and it was the one terminal
          // state in this module that reported nothing at all.
          //
          // By the time this fires, `attachMedia` has succeeded: a MediaSource is
          // attached to the element and its object URL is in `src`. A throw here leaves
          // exactly that — an attached MediaSource with NO manifest ever requested. The
          // element never errors (nothing native failed), `onCanPlay` never fires, and
          // the component sits on its spinner forever. Indistinguishable, from outside,
          // from a slow stream.
          //
          // 🪤 And D-157's census could not see it BY CONSTRUCTION. That census derives
          // its population from `reportEngineFailure(` call sites — so it enumerates
          // reasons that ARE reported and is structurally blind to a terminal state that
          // reports none. A derived gate is only as wide as the thing it derives from;
          // this is the PS-22 class the whole reporting layer exists to end, hiding
          // inside the layer's own coverage check.
          //
          // So: report, tear down, notify — the same contract every other terminal
          // branch follows, which also brings this state INTO the census.
          reportEngineFailure(
            'load_source_threw',
            e && e.message,
            url,
            mediaEl[ENGINE_AT_ATTACH]
          );
          teardownHls(mediaEl);
          notifyUnrecoverable(mediaEl);
        }
      });
      // hls.js fatal-error handling (async — guarded directly): recover the
      // media pipeline on a media error (BOUNDED by MAX_MEDIA_RECOVERY so a
      // persistently-broken stream can't loop), reload on a network error, and
      // DESTROY on any other fatal (mux/other/key-system) that has no recovery
      // path so the instance can't leak. Non-fatal errors are hls.js's own.
      let mediaRecoveryCount = 0;
      let networkRetryCount = 0;
      hls.on(Hls.Events.ERROR, (evt, data) => {
        try {
          if (!data || !data.fatal) return;
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            if (mediaRecoveryCount < MAX_MEDIA_RECOVERY) {
              mediaRecoveryCount += 1;
              hls.recoverMediaError();
            } else {
              // Give-up, not a hiccup: recovery is exhausted and this element
              // will stay silent. Reported once (PS-23) — the recovering
              // attempts above deliberately stay quiet.
              reportEngineFailure(
                'media_recovery_exhausted',
                `${MAX_MEDIA_RECOVERY} media-error recoveries exhausted`,
                url,
                mediaEl[ENGINE_AT_ATTACH]
              );
              teardownHls(mediaEl); // give up — break the recover loop
              notifyUnrecoverable(mediaEl); // D-134: let the component clear its spinner
            }
          } else if (data.type === Hls.ErrorTypes.NETWORK_ERROR
            && /^manifest/i.test(String((data && data.details) || ''))) {
            // 🔴 D-139 — a fatal MANIFEST error is TERMINAL in practice, and treating
            // it as a retry left the listener staring at "Buffering..." forever.
            //
            // MEASURED with real hls.js in real chromium against a 404 manifest:
            // `hls.startLoad()` produced **ZERO further HTTP requests** — 1 manifest
            // hit total across both a 15 s and a 30 s window. So the retry the branch
            // below performs does not happen for this class of error, and because
            // `networkRetryCount` therefore reaches 1 while NETWORK_RETRY_REPORT_AT is
            // 10, `network_retry_flood` was UNREACHABLE too: no UI signal AND no
            // telemetry. v2's play control was left permanently inert — exactly the
            // D-133 defect, still open on the live path after D-134 was supposed to
            // close it.
            //
            // 🪤 D-134 notified on only 2 of the terminal give-up reasons and missed the commonest
            // real live failure. Seventh one-of-N instance in this work, inside the fix
            // for the sixth.
            //
            // ⚠️ Scoped to `manifest*` deliberately: that is what was MEASURED.
            // `levelLoad*` and `fragLoad*` fatal network errors are NOT covered here
            // because nobody has measured whether `startLoad()` recovers them, and
            // guessing would either resurrect this bug or break a working retry.
            // Recorded as open rather than assumed.
            reportEngineFailure(
              'manifest_unrecoverable',
              String((data && data.details) || 'manifest error'),
              url,
              mediaEl[ENGINE_AT_ATTACH]
            );
            teardownHls(mediaEl);
            notifyUnrecoverable(mediaEl);
          } else if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            networkRetryCount += 1;
            // 🔑 What guarantees "one report" is reportEngineFailure's OWN suppression,
            // keyed on reason+url with a freshness window — NOT this `===`. A review of
            // this fix measured that: swapping `===` for `>=` changes no observable
            // behaviour, because every call past the threshold is suppressed there.
            // The `===` is kept as a cheap second bound (it stops the work, not just
            // the emit) but it is NOT the mechanism, and a test asserting "reported
            // once" is exercising the rate limiter. Do not read it as load-bearing.
            if (networkRetryCount === NETWORK_RETRY_REPORT_AT) {
              reportEngineFailure(
                'network_retry_flood',
                `${NETWORK_RETRY_REPORT_AT} fatal network errors, still retrying`,
                url,
                mediaEl[ENGINE_AT_ATTACH]
              );
            }
            hls.startLoad();
          } else {
            // mux/other/key-system: no recovery path exists, so this is
            // terminal for the element (PS-23).
            reportEngineFailure('fatal_unrecoverable', data.type, url, mediaEl[ENGINE_AT_ATTACH]);
            // D-143 — reordered to teardown-then-notify, matching every other
            // terminal branch. It was notify-then-teardown here and the two orders
            // were only ever LATENT-safe: this branch runs from hls.js's own error
            // handler, so the native listener whose re-entry the order protects
            // against was never installed. Uniform now, so the contract is one rule.
            teardownHls(mediaEl); // unrecoverable fatal — don't leak the instance
            notifyUnrecoverable(mediaEl); // D-134
          }
        } catch (e) {
          // 🔴 D-161's SIBLING. This was a bare swallow — the second of two, and the one
          // that processes every fatal error. A throw here (a hostile recoverMediaError,
          // an engine quirk, a `Hls.ErrorTypes` shape change) left the element dead with
          // ZERO telemetry: no report, no teardown, no notify, and the component's spinner
          // never clearing. D-161 closed exactly this state for the MEDIA_ATTACHED catch
          // and left it open here.
          //
          // Report, then follow the same teardown-then-notify order every other terminal
          // branch uses, so a failure INSIDE the error handler still ends with the element
          // in a defined state and the component told about it.
          reportEngineFailure(
            'error_handler_threw',
            (e && e.message) || 'error handler threw',
            url,
            mediaEl[ENGINE_AT_ATTACH]
          );
          try {
            teardownHls(mediaEl);
            notifyUnrecoverable(mediaEl);
          } catch (e2) {
            // Last resort. If teardown ITSELF throws we have already reported, which is
            // the part an operator needs; swallowing here keeps a second failure from
            // replacing the first as the visible one.
          }
        }
      });
      // 🔑 Autoplay is gated on the session still being ours. If a handler above tore
      // down synchronously, the element has no engine and no source — playing it would
      // start a `play()` on a detached element and produce a rejection the listener
      // sees as a dead control.
      if (options.autoplay && mediaEl.__hlsInstance === hls) {
        startPlayback(mediaEl);
      }
      return;
    }

    // Fallback: native HLS (iOS Safari plays `.m3u8` from a plain `src`).
    //
    // ⚠️ MEASURED 2026-08-06, and it corrects what PS-22/PS-23 assumed: Chromium
    // answers `'maybe'` to this MIME while being unable to decode the stream. So
    // when the engine is missing on Chrome, control reaches HERE — not the
    // `no engine` branch below, which those entries named as the dead-engine
    // path. The element takes the manifest as a plain src, fails to decode, and
    // the only trace is a `play()` rejection the caller logs as a WARNING.
    // That is the silent outage PS-22 actually shipped, so it needs its own
    // signal: watch for the element rejecting the source.
    if (mediaEl.canPlayType && mediaEl.canPlayType('application/vnd.apple.mpegurl')) {
      if (typeof mediaEl.addEventListener === 'function') {
        // Tracked on the element so teardownHls can remove it. `{ once: true }`
        // only removes a listener when it FIRES, so without this every
        // re-attach (episode switch, drop-to-live) would pile up another
        // listener that may never run — an unbounded leak on a long-lived
        // embed (review fold).
        const onNativeError = () => {
          // Self-disarming, precisely: a listener whose captured `url` is no longer
          // the element's current `dataset.hlsSrc` stays quiet.
          //
          // ⚠️ That is NOT the same as "after a detach/re-attach", which is what this
          // comment used to claim and is false for the drop-to-live path: re-attaching
          // to the SAME url restores the same value, so a surviving old listener would
          // still match. It stays quiet there because teardownHls REMOVES it (D-74 made
          // that unconditional). Two mechanisms, and the comment named the wrong one.
          if (mediaEl.dataset && mediaEl.dataset.hlsSrc === url) {
            reportEngineFailure(
              'native_hls_failed',
              'the browser accepted the HLS MIME type but could not play the stream',
              url,
              mediaEl[ENGINE_AT_ATTACH]
            );
          }
        };
        mediaEl.__hlsNativeErrorHandler = onNativeError;
        mediaEl.addEventListener('error', onNativeError, { once: true });
      }
      mediaEl.setAttribute('src', url);
      mediaEl.dataset.hlsSrc = url;
      if (typeof mediaEl.load === 'function') {
        mediaEl.load();
      }
      if (options.autoplay) {
        startPlayback(mediaEl);
      }
      return;
    }

    // Neither engine available. Still a no-op for the caller (matching the
    // guarded Viblast absence), but no longer a SILENT one: this is the exact
    // state PS-22 shipped in for the whole port — the vendored engine never
    // arrived, and nothing anywhere said so. Reported once per page load.
    reportEngineFailure(
      'no_engine',
      'window.Hls is undefined and the browser has no native HLS',
      url,
      mediaEl[ENGINE_AT_ATTACH]
    );
    // 🔴 D-143 — this is a TERMINAL give-up and it used to tell the component
    // nothing. Reporting telemetry and returning left `isLoading` true with no
    // event that could ever clear it: `mediaEl.error` stays null here (nothing
    // native failed), so `onMediaError` cannot fire on its own.
    //
    // ⚠️ Not a hypothetical branch — it is the SHIPPED off-master state. The
    // vendored hls.js mirror was never uploaded (!207), so every live embed on an
    // engine that answers '' to the HLS MIME type lands exactly here.
    //
    // The teardown is a no-op today (the stop-before-attach above already ran and
    // this branch attaches nothing), and it is here for the CONTRACT: every
    // terminal reason tears down and then notifies, in that order, so the guard
    // that derives this population from the module has one rule to check.
    teardownHls(mediaEl);
    notifyUnrecoverable(mediaEl);
  } catch (e) {
    // Swallow — an iOS quirk must not surface. Mirrors v4's old `typeof`-guard
    // on the Viblast global + try/catch. Reported once (PS-23) so that a throw
    // here is not the new silent failure.
    reportEngineFailure('attach_threw', e && e.message, url, mediaEl[ENGINE_AT_ATTACH]);
    // 🔴 D-143 — same gap, and here the teardown is load-bearing rather than
    // contractual. A throw can land after the native branch has parked its `error`
    // listener on the element, or after `dataset.hlsSrc` was written, or after
    // `__hlsInstance` was assigned. Without this, that bookkeeping survives and
    // blocks the next re-attach, whose `attachedSrc !== audioSrc` compare then
    // reads a source that is not attached to anything.
    //
    // 🪤 Those are three DIFFERENT points, and an earlier version of this comment
    // ran them together as "parked its listener AND written `dataset.hlsSrc`",
    // offering a throwing `setAttribute` as the example. `setAttribute` (:592) runs
    // BEFORE `dataset.hlsSrc = url` (:593), so it is the one case where the listener
    // is parked and the dataset is NOT — and therefore the one case where the
    // re-entry hazard below is already disarmed. The conjunction holds for a throwing
    // `load()`/`play()`; it is false for the example it cited.
    //
    // 🔑 Order is teardown-then-notify and that is not cosmetic: `teardownHls`
    // REMOVES the native `error` listener, so notifying first would re-enter
    // `onNativeError` and emit a spurious `native_hls_failed` for a failure that was
    // not native — reachable when the throw lands at `load()`/`play()`, i.e. after
    // `dataset.hlsSrc` is set and the listener's own guard would match.
    teardownHls(mediaEl);
    notifyUnrecoverable(mediaEl);
  }
};

/**
 * Detach any hls.js session attached by {@link attachLiveStream}: destroy the
 * instance, drop the reference, and clear `dataset.hlsSrc`. Idempotent and
 * throw-safe — a second call, or a call on an element that never had a session,
 * is a safe no-op. Replaces the old `viblast(el).stop()`.
 *
 * @param {HTMLMediaElement} mediaEl the `<audio>`/`<video>` element
 * @return {void}
 */
export const detachLiveStream = (mediaEl) => {
  teardownHls(mediaEl);
};
